// GuestsReport — guest book + top repeat guests.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReportShell from "./ReportShell";
import { Panel, Loading, fmtMoney } from "./OverviewReport";
import { StatTile, BarChart } from "../../../components/charts";
import { guestsUrl, reservationsUrl } from "../../../BaseURL";

export default function GuestsReport({ range, onRangeChange }) {
  const [guests, setGuests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [g, r] = await Promise.all([
          axios.get(guestsUrl),
          axios.get(reservationsUrl, {
            params: { from: range.from || undefined, to: range.to || undefined },
          }),
        ]);
        if (cancelled) return;
        setGuests(g.data?.results || []);
        setReservations(r.data?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  const stats = useMemo(() => {
    // For each guest, sum stays + spend (within the date-range view).
    const byGuest = new Map();
    for (const r of reservations) {
      const g = byGuest.get(r.guest_id) || { id: r.guest_id, stays: 0, spend: 0, lastStay: null };
      g.stays += 1;
      g.spend += Number(r.paid_amount) || 0;
      const dt = r.check_in_at || r.check_in_date;
      if (!g.lastStay || (dt && dt > g.lastStay)) g.lastStay = dt;
      byGuest.set(r.guest_id, g);
    }

    // Augment with guest profile data + name for the chart.
    const enriched = Array.from(byGuest.values()).map(g => {
      const profile = guests.find(x => x.id === g.id) || {};
      return { ...g, name: profile.full_name || "Unknown guest", phone: profile.phone, email: profile.email };
    });

    const top = [...enriched].sort((a, b) => b.stays - a.stays).slice(0, 10);
    const totalGuests = guests.length;
    const activeInWindow = enriched.length;
    const repeatGuests = enriched.filter(g => g.stays > 1).length;
    const totalSpend = enriched.reduce((s, g) => s + g.spend, 0);

    return {
      enriched, top, totalGuests, activeInWindow, repeatGuests, totalSpend,
      topChart: top.map(g => ({ label: g.name.split(" ")[0], value: g.stays })),
    };
  }, [guests, reservations]);

  const csv = useMemo(() => ({
    headers: ["Guest","Phone","Email","Stays in range","Total spend (GH₵)","Last stay","Total reservations (all time)"],
    rows: guests.map(g => {
      const inWindow = stats.enriched.find(x => x.id === g.id);
      return [
        g.full_name, g.phone || "", g.email || "",
        inWindow ? inWindow.stays : 0,
        inWindow ? inWindow.spend.toFixed(2) : "0.00",
        inWindow?.lastStay ? String(inWindow.lastStay).slice(0, 10) : (g.last_stay ? String(g.last_stay).slice(0, 10) : ""),
        g.stay_count || 0,
      ];
    }),
  }), [guests, stats.enriched]);

  return (
    <ReportShell
      title="Guests Report"
      subtitle="Guest book + repeat-stay analysis"
      range={{ ...range, onChange: onRangeChange }}
      csv={csv}
      filename="guests"
    >
      {loading ? <Loading/> : (
        <>
          <div className="kpi-row">
            <StatTile icon="👥" label="Guests in Range" value={stats.activeInWindow}
              sub={`${stats.totalGuests} on file`} />
            <StatTile icon="🔁" label="Repeat Guests"  value={stats.repeatGuests}
              sub={stats.activeInWindow > 0 ? `${Math.round((stats.repeatGuests / stats.activeInWindow) * 100)}% of active` : "0% of active"}
              accent="#4a7c59" />
            <StatTile icon="💰" label="Total Spend"    value={fmtMoney(stats.totalSpend)} accent="#c8a05a" />
            <StatTile icon="🏆" label="Top Spender"
              value={stats.enriched.length ? [...stats.enriched].sort((a, b) => b.spend - a.spend)[0].name : "—"}
              sub={stats.enriched.length ? fmtMoney([...stats.enriched].sort((a, b) => b.spend - a.spend)[0].spend) : ""}
              accent="#a8814a" />
          </div>

          <Panel title="Top Repeat Guests" style={{ marginTop: 16 }}>
            <BarChart data={stats.topChart} valueFormat={(n) => `${n} stays`} />
          </Panel>

          <Panel title={`Guest Book (${guests.length})`} style={{ marginTop: 18 }}>
            <table className="tbl" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Name</th><th>Phone</th><th>Email</th>
                  <th style={{ textAlign: "center" }}>Stays in range</th>
                  <th style={{ textAlign: "right" }}>Spend in range</th>
                  <th>Last stay</th>
                </tr>
              </thead>
              <tbody>
                {guests.map(g => {
                  const w = stats.enriched.find(x => x.id === g.id);
                  return (
                    <tr key={g.id}>
                      <td style={{ fontWeight: 500 }}>{g.full_name}</td>
                      <td>{g.phone || "—"}</td>
                      <td>{g.email || "—"}</td>
                      <td style={{ textAlign: "center" }}>{w?.stays || 0}</td>
                      <td style={{ textAlign: "right" }}>{fmtMoney(w?.spend || 0)}</td>
                      <td>{w?.lastStay ? String(w.lastStay).slice(0, 10) : (g.last_stay ? String(g.last_stay).slice(0, 10) : "—")}</td>
                    </tr>
                  );
                })}
                {guests.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#8a7060", fontStyle: "italic" }}>
                    No guests on file yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </ReportShell>
  );
}
